import React, { useState, useEffect } from "react";
import { PlaidLink } from "react-plaid-link";
import FundingSourceList from "Components/FundingSources/FundingSourceList";
import { useHistory } from "react-router-dom";
import { endingIn } from "Utils/Utils";
import "Pages/Enrollment/BankAccount.scss";
import Loading from "Components/Loading";
import useVipApiFetcher, {
  handleError,
  handleResponse,
  handleOkResponse,
} from "Utils/Hooks/useVipApiFetcher";
import useSession from "Utils/Hooks/useSession";
import { Routes, PRODUCTTYPES, ZEROBALANCEREASON } from "Utils/Constants";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useTransactionInitiated from "Utils/Hooks/useTransactionInitiated";
import useFundType, { FUNDTYPE } from "Utils/Hooks/useFundType";
import useRedirectUrl from "Utils/Hooks/useRedirectUrl";
import useOAuthStateId from "Utils/Hooks/useOAuthStateId";
import useAppKey from "Utils/Hooks/useAppKey";
import { faro } from "@grafana/faro-react";
import { Box } from "@mui/material";
import { Button, Stack } from "@pav/pav-products-design-system";

const AddAccount = (props) => {
  const vipApi = useVipApiFetcher();
  const history = useHistory();
  const { session } = useSession();
  const mode = useMode();
  const fundType = useFundType();
  const url = useRedirectUrl();
  const appKey = useAppKey();
  const oauth_state_id = useOAuthStateId();
  const { transactionInitiated } = useTransactionInitiated();
  const isDepositMode = mode === MODE.deposit;

  const [submitted, setSubmitted] = useState(false);
  const [linkToken, setLinkToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [receivedRedirectUri, setReceivedRedirectUri] = useState(null);
  const openPlaidModal =
    !session.toggles.finicityEnabled &&
    session.toggles.plaidEnabled &&
    session.toggles.plaidDefault;
  const isVIPPreferred = session?.patronProductType === PRODUCTTYPES.PREFERRED;
  const productType = isVIPPreferred ? "VIP Preferred" : "VIP Online";
  const onlyPlaidFundingSource = window.sessionStorage.getItem("onlyPlaid")
    ? window.sessionStorage.getItem("onlyPlaid")
    : false;
  const onPlaidSuccess = async (_, metadata) => {
    await onPlaidCommon(
      {
        accountId: metadata.account_id,
        accountToken: metadata.public_token,
      },
      "/api/bank/plaid",
    );
  };

  const productTypeTitle =
    session?.patronProductType === PRODUCTTYPES.PREFERRED ? "VIPP" : "VIPO";
  const nobank = props.location.state && props.location.state.nobank;
  document.title = productTypeTitle + " - Add Account";

  useEffect(() => {
    if (
      props.plaidRedirect &&
      !(props.location.state && props.location.state.notRedirect)
    ) {
      setLinkToken(localStorage.getItem("link_token"));
      setReceivedRedirectUri(
        url ? url + "?oauth_state_id=" + oauth_state_id : window.location.href,
      );
      // setReceivedRedirectUri("https://cdn.plaid.com/link/v2/stable/oauth.html?oauth_state_id=" + oauth_state_id);
      return;
    }
    if (props.location.state && props.location.state.notRedirect) {
      setIsLoading(false);
    }
    if (props.isCashierRedirect) {
      setIsLoading(false);
    }
    if (openPlaidModal) {
      if (!props.plaidRedirect) {
        let mounted = true;
        let oprUrl = "";
        if (url) {
          oprUrl = appKey
            ? "?redirectUri=" + url + "/" + appKey
            : "?redirectUri=" + url;
        } else {
          oprUrl = appKey
            ? "?redirectUri=" + window.location.href + "/" + appKey
            : "?redirectUri=" + window.location.href + "/" + session.merchantId;
        }
        (async () => {
          try {
            await vipApi.fetch("/api/plaid/link" + oprUrl).then((res) => {
              res.json().then((data) => {
                if (!mounted) return;
                if (res.ok) {
                  setLinkToken(data.linkToken);
                  localStorage.setItem("link_token", data.linkToken);
                }
                if (!res.ok) {
                  faro.api.pushError(new Error("Plaid Link Token Error"), {
                    context: data,
                  });
                }
              });
            });
          } catch (error) {
            faro.api.pushError(
              new Error("Add Account Plaid Link Token Network Error"),
              {
                context: error,
              },
            );
          }
        })();
        return () => {
          mounted = false;
        };
      }
    }
  }, []);

  const onPlaidCommon = async (body, url = "/api/bank") => {
    setSubmitted(true);
    const customErrorButtons =
      mode === MODE.manageBanks && transactionInitiated
        ? [
            {
              label: "Try Again",
              route: Routes.MY_ACCOUNTS,
            },
          ]
        : transactionInitiated
          ? [
              {
                label: "Try Again",
                route: Routes.MY_ACCOUNTS,
              },
              {
                label: `Complete ${isDepositMode ? "Deposit" : "Withdraw"}`,
                route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
              },
            ]
          : undefined;

    vipApi
      .fetch(url, "POST", body)
      .then((response) => handleResponse(history, response))
      .then((response) => {
        if (response.status === 200) {
          history.push({
            pathname:
              mode === MODE.manageBanks
                ? "/addAccountSuccess"
                : isDepositMode
                  ? "/addFunds"
                  : "/withdrawFunds",
          });
        } else {
          let isZeroBalance =
            response.body?.operatorErrorMessage.includes(ZEROBALANCEREASON);
          let message = isZeroBalance
            ? "The bank account you are trying to connect must have more than $0."
            : (response.body.patronErrorMessage ??
              "We are unable to complete this request. Please contact our Customer Service Team at 1-800-500-1973. Reference code E-29 so that we may better serve you.");
          let zeroBalanceErrorButtons = !transactionInitiated
            ? [
                {
                  label: "Manage Funding Sources",
                  route: Routes.MY_ACCOUNTS,
                  icon: true,
                },
                {
                  label: `Cancel`,
                  route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
                  icon: false,
                  zeroBal: true,
                },
              ]
            : [
                {
                  label: "Manage Funding Sources",
                  route: Routes.MY_ACCOUNTS,
                  icon: true,
                },
              ];
          if (isZeroBalance) {
            faro.api.pushError(
              new Error("Add Account Plaid Zero Account Balance"),
              { context: response },
            );
            history.push({
              pathname: "/zerobalance",
              state: {
                isError: false,
                message: message,
                showButton: true,
                allowRetry: false,
                customButtons: zeroBalanceErrorButtons,
                transactionInitiated: false,
              },
            });
          } else {
            faro.api.pushError(new Error("Add Account Plaid Error"), {
              context: response,
            });
            history.push({
              pathname: "/error",
              state: {
                isError: true,
                message: message,
                showButton: true,
                allowRetry: false,
                customButtons: customErrorButtons,
                transactionInitiated: false,
              },
            });
          }
        }
      })
      .catch((error) => {
        faro.api.pushError(new Error("Add Account Plaid Network Error"), {
          context: error,
        });
        handleError(error, history, customErrorButtons);
      });
    localStorage.removeItem("link_token");
  };

  const onSubmitCommon = async (
    accountNumberDisplay,
    body,
    url = "/api/bank",
  ) => {
    setSubmitted(true);

    const customErrorButtons =
      mode === MODE.manageBanks && transactionInitiated
        ? [
            {
              label: "Try Again",
              route: Routes.MY_ACCOUNTS,
            },
          ]
        : transactionInitiated
          ? [
              {
                label: "Try Again",
                route: Routes.MY_ACCOUNTS,
              },
              {
                label: `Complete ${isDepositMode ? "Deposit" : "Withdraw"}`,
                route: isDepositMode ? Routes.DEPOSIT : Routes.WITHDRAW,
              },
            ]
          : undefined;

    vipApi
      .fetch(url, "POST", body)
      .then((response) => handleResponse(history, response))
      .then((response) =>
        handleOkResponse({
          history,
          response,
          title: "Funding Source Added", //`${fundTypeName} Added`,
          message: `You successfully added your ${response.body.bankName} account${endingIn(accountNumberDisplay)} to your ${productType} account.`,
          errorCustomButtons: customErrorButtons,
        }),
      )
      .catch((error) => {
        faro.api.pushError(new Error("Add Account Submission Network Error"), {
          context: error,
        });
        handleError(error, history, customErrorButtons);
      });
    localStorage.removeItem("link_token");
  };

  const onSuccessDefault = async (_, metadata) => {
    try {
      await vipApi
        .fetch("/api/plaid/account", "PUT", {
          accountId: metadata.account_id,
          accountToken: metadata.public_token,
        })
        .then((res) => res.json())
        .then((data) => {
          onSubmitCommon(metadata.account.mask, {
            session: data,
            metadata: metadata,
          });
        });
    } catch (error) {
      faro.api.pushError(new Error("Add Account Plaid Update Network Error"), {
        context: error,
      });
    }
  };

  let fundingSourceProps = {};

  if (fundType === FUNDTYPE.ach || !fundType || session.transactionSucceeded) {
    fundingSourceProps["bankProps"] = {
      onDataChanged: onSubmitCommon,
    };
    if (session.toggles.plaidEnabled) {
      fundingSourceProps["plaidProps"] = {
        onSuccess: onPlaidSuccess,
      };
    }
  }

  if (
    fundType === FUNDTYPE.netspend ||
    !fundType ||
    session.transactionSucceeded
  ) {
    fundingSourceProps["netspendProps"] = {
      onDataChanged: onSubmitCommon,
    };
  }

  fundingSourceProps["plaidDefault"] =
    session.toggles.plaidEnabled && session.toggles.plaidDefault;

  fundingSourceProps["isEnrollment"] = false;

  if (fundType === FUNDTYPE.netspend) {
    fundingSourceProps["showManualCTA"] = false;
    fundingSourceProps["showNetspendCTA"] = true;
  } else {
    if (
      session?.patronProductType === PRODUCTTYPES.ONLINE ||
      onlyPlaidFundingSource
    ) {
      fundingSourceProps["showManualCTA"] = false;
    } else {
      fundingSourceProps["showManualCTA"] = true;
    }
    fundingSourceProps["showNetspendCTA"] = false;
  }
  fundingSourceProps["nobank"] = nobank;
  fundingSourceProps["finicityEnabled"] = session.toggles.finicityEnabled;
  fundingSourceProps["noWarranty"] = session.noWarranty;
  fundingSourceProps["operatorBoth"] =
    session.operatorProducts.includes(PRODUCTTYPES.PREFERRED) &&
    session.operatorProducts.includes(PRODUCTTYPES.ONLINE);

  const style = {
    width: "100%",
    marginTop: "20px",
  };

  const loadHandler = () => {
    window.sessionStorage.setItem("plaidlaunch", true);
    if (openPlaidModal || props.plaidRedirect) {
      const open = document.getElementsByClassName("ready")[0];
      open.click();
      setIsLoading(false);
    }
  };

  const onExit = (error, metadata) => {
    window.sessionStorage.setItem("plaidlaunch", false);
  };

  return (
    <>
      {(openPlaidModal || props.isCashierRedirect) && (
        <PlaidLink
          token={linkToken}
          onSuccess={onPlaidSuccess ?? onSuccessDefault}
          className="ready"
          onLoad={loadHandler}
          receivedRedirectUri={receivedRedirectUri}
          onExit={onExit}
        ></PlaidLink>
      )}
      {props.plaidRedirect && (
        <PlaidLink
          token={linkToken}
          onSuccess={onPlaidSuccess ?? onSuccessDefault}
          className="ready"
          onLoad={loadHandler}
          receivedRedirectUri={receivedRedirectUri}
          onExit={onExit}
        ></PlaidLink>
      )}
      {(openPlaidModal || props.plaidRedirect || props.isCashierRedirect) &&
      isLoading ? (
        <Loading />
      ) : (
        <Stack className="manage-account">
          <Box
            className="manage-account-title"
            sx={{
              textAlign: { xs: "left", md: "center" },
            }}
          >
            {nobank ? "Account Created" : "Add Funding Source"}
          </Box>
          {submitted ? (
            <Loading />
          ) : (
            <FundingSourceList {...fundingSourceProps} />
          )}
          {transactionInitiated && (
            <Box sx={{ marginTop: "var(--spacing-md)" }}>
              <Button
                buttonType="secondary"
                fullWidth
                onClick={() => {
                  history.push(Routes.MY_ACCOUNTS);
                }}
              >
                Manage Funding Sources
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </>
  );
};

export default AddAccount;
